/**
 * Security Service for TradeTitans
 * Handles device authentication, session management, and security mechanisms
 * to prevent account sharing and ensure single device/multi-device compliance
 */

import { supabase } from '@/lib/supabase';
import { getDeviceInfo, getDeviceFingerprint, getLocationData } from '@/lib/deviceUtils';
import { 
  DeviceSession, 
  SecurityEvent, 
  SecurityEventType, 
  DeviceInfo, 
  LocationData,
  SecuritySettings,
  SubscriptionTier,
  SUBSCRIPTION_TIERS
} from '@/lib/subscriptionTypes';

export interface LoginRequest {
  email: string;
  password: string;
  deviceInfo?: DeviceInfo;
  locationData?: LocationData;
}

export interface LoginResponse {
  success: boolean;
  session?: DeviceSession;
  error?: string;
  requiresDeviceVerification?: boolean;
  blockedDevices?: string[];
}

export interface DeviceVerificationRequest {
  userId: string;
  tenantId: string;
  deviceFingerprint: string;
  verificationCode: string;
}

export class SecurityService {
  private static instance: SecurityService;
  private currentSession: DeviceSession | null = null;

  static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
    }
    return SecurityService.instance;
  }

  /**
   * Authenticate user with device security
   */
  async authenticateUser(request: LoginRequest): Promise<LoginResponse> {
    try {
      // Get device information
      const deviceInfo = request.deviceInfo || getDeviceInfo();
      const deviceFingerprint = await getDeviceFingerprint();
      const locationData = request.locationData || await getLocationData();

      // Authenticate with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: request.email,
        password: request.password
      });

      if (authError) {
        return {
          success: false,
          error: authError.message
        };
      }

      if (!authData.user) {
        return {
          success: false,
          error: 'Authentication failed'
        };
      }

      // Get user profile and tenant
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*, tenants(*)')
        .eq('id', authData.user.id)
        .single();

      if (!profile || !profile.tenants) {
        return {
          success: false,
          error: 'User profile or tenant not found'
        };
      }

      const tenantId = profile.tenants.id;

      // Get current subscription
      const { data: subscription } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .single();

      if (!subscription) {
        return {
          success: false,
          error: 'No active subscription found'
        };
      }

      const securitySettings = SUBSCRIPTION_TIERS[subscription.tier].security;

      // Check device limits
      const deviceCheck = await this.checkDeviceLimits(
        authData.user.id,
        tenantId,
        deviceFingerprint,
        securitySettings
      );

      if (!deviceCheck.allowed) {
        return {
          success: false,
          error: deviceCheck.reason || 'Device limit exceeded',
          blockedDevices: deviceCheck.blockedDevices
        };
      }

      // Create or update device session
      const session = await this.createDeviceSession({
        userId: authData.user.id,
        tenantId,
        deviceFingerprint,
        deviceInfo,
        locationData,
        securitySettings
      });

      // Log security event
      await this.logSecurityEvent({
        userId: authData.user.id,
        tenantId,
        eventType: SecurityEventType.DEVICE_LOGIN,
        deviceFingerprint,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        locationData,
        eventData: {
          subscriptionTier: subscription.tier,
          deviceInfo
        }
      });

      this.currentSession = session;

      return {
        success: true,
        session,
        requiresDeviceVerification: securitySettings.deviceVerificationRequired
      };

    } catch (error) {
      console.error('Authentication error:', error);
      return {
        success: false,
        error: 'Authentication failed'
      };
    }
  }

  /**
   * Check device limits based on subscription tier
   */
  private async checkDeviceLimits(
    userId: string,
    tenantId: string,
    deviceFingerprint: string,
    securitySettings: SecuritySettings
  ): Promise<{
    allowed: boolean;
    reason?: string;
    blockedDevices?: string[];
  }> {
    try {
      // Get current active sessions for this user
      const { data: activeSessions } = await supabase
        .from('device_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('is_active', true);

      const currentSessions = activeSessions || [];
      const currentDeviceCount = currentSessions.length;

      // Check if device is already registered
      const existingDevice = currentSessions.find(
        session => session.device_fingerprint === deviceFingerprint
      );

      if (existingDevice) {
        // Device is already registered, allow login
        return { allowed: true };
      }

      // Check device limits
      if (currentDeviceCount >= securitySettings.maxConcurrentDevices) {
        // Force logout other devices if required
        if (securitySettings.forceLogoutOnNewDevice) {
          await this.forceLogoutOtherDevices(userId, tenantId);
          return { allowed: true };
        } else {
          return {
            allowed: false,
            reason: `Maximum devices (${securitySettings.maxConcurrentDevices}) reached. Please log out from another device first.`,
            blockedDevices: currentSessions.map(s => s.device_fingerprint)
          };
        }
      }

      return { allowed: true };

    } catch (error) {
      console.error('Device limit check error:', error);
      return { allowed: false, reason: 'Error checking device limits' };
    }
  }

  /**
   * Create or update device session
   */
  private async createDeviceSession({
    userId,
    tenantId,
    deviceFingerprint,
    deviceInfo,
    locationData,
    securitySettings
  }: {
    userId: string;
    tenantId: string;
    deviceFingerprint: string;
    deviceInfo: DeviceInfo;
    locationData?: LocationData;
    securitySettings: SecuritySettings;
  }): Promise<DeviceSession> {
    try {
      const sessionToken = this.generateSessionToken();
      const expiresAt = new Date(Date.now() + securitySettings.sessionTimeoutMinutes * 60 * 1000);

      // Check if session already exists for this device
      const { data: existingSession } = await supabase
        .from('device_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('device_fingerprint', deviceFingerprint)
        .eq('is_active', true)
        .single();

      if (existingSession) {
        // Update existing session
        const { data: updatedSession, error } = await supabase
          .from('device_sessions')
          .update({
            session_token: sessionToken,
            last_activity: new Date().toISOString(),
            expires_at: expiresAt.toISOString(),
            device_info: deviceInfo,
            location_data: locationData
          })
          .eq('id', existingSession.id)
          .select()
          .single();

        if (error) throw error;
        return updatedSession;
      } else {
        // Create new session
        const { data: newSession, error } = await supabase
          .from('device_sessions')
          .insert({
            user_id: userId,
            tenant_id: tenantId,
            device_fingerprint: deviceFingerprint,
            device_info: deviceInfo,
            ip_address: await this.getClientIP(),
            user_agent: navigator.userAgent,
            location_data: locationData,
            session_token: sessionToken,
            is_active: true,
            last_activity: new Date().toISOString(),
            expires_at: expiresAt.toISOString()
          })
          .select()
          .single();

        if (error) throw error;
        return newSession;
      }

    } catch (error) {
      console.error('Session creation error:', error);
      throw error;
    }
  }

  /**
   * Force logout other devices
   */
  private async forceLogoutOtherDevices(userId: string, tenantId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('device_sessions')
        .update({
          is_active: false,
          last_activity: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('is_active', true);

      if (error) throw error;

      // Log security event
      await this.logSecurityEvent({
        userId,
        tenantId,
        eventType: SecurityEventType.DEVICE_LOGOUT,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        eventData: { reason: 'forced_logout_new_device' }
      });

    } catch (error) {
      console.error('Force logout error:', error);
      throw error;
    }
  }

  /**
   * Validate current session
   */
  async validateSession(sessionToken: string): Promise<{
    valid: boolean;
    session?: DeviceSession;
    error?: string;
  }> {
    try {
      const { data: session, error } = await supabase
        .from('device_sessions')
        .select('*')
        .eq('session_token', sessionToken)
        .eq('is_active', true)
        .single();

      if (error || !session) {
        return { valid: false, error: 'Session not found' };
      }

      // Check if session is expired
      if (new Date(session.expires_at) < new Date()) {
        await this.invalidateSession(session.id);
        return { valid: false, error: 'Session expired' };
      }

      // Update last activity
      await supabase
        .from('device_sessions')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', session.id);

      return { valid: true, session };

    } catch (error) {
      console.error('Session validation error:', error);
      return { valid: false, error: 'Session validation failed' };
    }
  }

  /**
   * Logout user from current device
   */
  async logout(sessionToken: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: session } = await supabase
        .from('device_sessions')
        .select('*')
        .eq('session_token', sessionToken)
        .single();

      if (session) {
        await this.invalidateSession(session.id);

        // Log security event
        await this.logSecurityEvent({
          userId: session.user_id,
          tenantId: session.tenant_id,
          eventType: SecurityEventType.DEVICE_LOGOUT,
          deviceFingerprint: session.device_fingerprint,
          ipAddress: await this.getClientIP(),
          userAgent: navigator.userAgent,
          eventData: { reason: 'user_logout' }
        });
      }

      this.currentSession = null;
      return { success: true };

    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: 'Logout failed' };
    }
  }

  /**
   * Invalidate session
   */
  private async invalidateSession(sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('device_sessions')
        .update({
          is_active: false,
          last_activity: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) throw error;

    } catch (error) {
      console.error('Session invalidation error:', error);
      throw error;
    }
  }

  /**
   * Get active sessions for user
   */
  async getActiveSessions(userId: string, tenantId: string): Promise<DeviceSession[]> {
    try {
      const { data: sessions, error } = await supabase
        .from('device_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('last_activity', { ascending: false });

      if (error) throw error;
      return sessions || [];

    } catch (error) {
      console.error('Get active sessions error:', error);
      return [];
    }
  }

  /**
   * Log security event
   */
  private async logSecurityEvent({
    userId,
    tenantId,
    eventType,
    deviceFingerprint,
    ipAddress,
    userAgent,
    locationData,
    eventData
  }: {
    userId: string;
    tenantId: string;
    eventType: SecurityEventType;
    deviceFingerprint?: string;
    ipAddress: string;
    userAgent: string;
    locationData?: LocationData;
    eventData?: any;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('security_events')
        .insert({
          user_id: userId,
          tenant_id: tenantId,
          event_type: eventType,
          device_fingerprint: deviceFingerprint,
          ip_address: ipAddress,
          user_agent: userAgent,
          location_data: locationData,
          event_data: eventData
        });

      if (error) {
        console.error('Security event logging error:', error);
      }

    } catch (error) {
      console.error('Security event logging error:', error);
    }
  }

  /**
   * Get client IP address
   */
  private async getClientIP(): Promise<string> {
    try {
      // In a real implementation, you would get this from your backend
      // For now, we'll use a placeholder
      return '127.0.0.1';
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Generate session token
   */
  private generateSessionToken(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  /**
   * Get current session
   */
  getCurrentSession(): DeviceSession | null {
    return this.currentSession;
  }

  /**
   * Check for suspicious activity
   */
  async checkSuspiciousActivity(
    userId: string,
    tenantId: string,
    deviceFingerprint: string,
    locationData?: LocationData
  ): Promise<{
    suspicious: boolean;
    reason?: string;
    actions?: string[];
  }> {
    try {
      // Get recent security events
      const { data: recentEvents } = await supabase
        .from('security_events')
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false });

      if (!recentEvents) return { suspicious: false };

      // Check for multiple failed login attempts
      const failedLogins = recentEvents.filter(
        event => event.event_type === SecurityEventType.DEVICE_VERIFICATION_FAILED
      );

      if (failedLogins.length >= 5) {
        return {
          suspicious: true,
          reason: 'Multiple failed login attempts detected',
          actions: ['block_device', 'require_verification']
        };
      }

      // Check for location anomalies (if location tracking is enabled)
      if (locationData) {
        const locationEvents = recentEvents.filter(
          event => event.event_type === SecurityEventType.LOCATION_CHANGE
        );

        if (locationEvents.length > 0) {
          const lastLocation = locationEvents[0].location_data;
          if (lastLocation) {
            const distance = this.calculateDistance(
              lastLocation.latitude,
              lastLocation.longitude,
              locationData.latitude,
              locationData.longitude
            );

            // If distance is more than 100km in less than 1 hour, it's suspicious
            if (distance > 100) {
              const timeDiff = Math.abs(locationData.timestamp - lastLocation.timestamp);
              if (timeDiff < 60 * 60 * 1000) { // 1 hour
                return {
                  suspicious: true,
                  reason: 'Unusual location change detected',
                  actions: ['require_verification']
                };
              }
            }
          }
        }
      }

      return { suspicious: false };

    } catch (error) {
      console.error('Suspicious activity check error:', error);
      return { suspicious: false };
    }
  }

  /**
   * Calculate distance between two points
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }
}

// Export singleton instance
export const securityService = SecurityService.getInstance();
